import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  AppBar,
  Toolbar,
  Paper,
  IconButton,
  TextField,
  Pagination,
  Collapse
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/styles';
import HistoryIcon from '@mui/icons-material/History';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Icon } from '@iconify/react';
import user_api from 'utils/api/users';
import { useSnackbar } from 'notistack5';
import Page from '../components/Page';
import { fDateTime } from '../utils/formatTime';
import storage from 'utils/storage';
import moment from 'moment-timezone';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '24px',
    '@media (max-width: 600px)': {
      padding: '16px',
    },
  },
  tabButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  },
  historyCard: {
    marginBottom: '16px',
    borderRadius: '8px',
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
    border: '1px solid #2194F3',
    '&:hover': { 
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)' 
    },
    transition: 'box-shadow 0.2s ease-in-out'
  },
  blueBackground: {
    backgroundColor: '#2194F3',
    color: 'white',
    borderRadius: '4px',
    padding: '8px',
    marginBottom: '8px'
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    gap: '12px',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '24px',
    marginBottom: '24px',
  }
}));

const EmployeeMyHistory = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [weeklyRecords, setWeeklyRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryPage, setSummaryPage] = useState(true);
  const [allShiftPage, setAllShiftPage] = useState(false);
  const [userData, setUserData] = useState(null);
  const [totalWeeklyHours, setTotalWeeklyHours] = useState(0);
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  
  // Search states
  const [searchDate, setSearchDate] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    // Update filtered records whenever records or search date changes
    if (records.length > 0) {
      if (searchDate) {
        const formattedSearchDate = moment(searchDate).format('YYYY-MM-DD');
        const filtered = records.filter(record => 
          moment(record.date).format('YYYY-MM-DD') === formattedSearchDate
        );
        setFilteredRecords(filtered);
        setTotalPages(Math.ceil(filtered.length / recordsPerPage));
        setIsFiltered(true);
      } else {
        setFilteredRecords(records);
        setTotalPages(Math.ceil(records.length / recordsPerPage));
        setIsFiltered(false);
      }
      // Reset to first page when search changes
      setPage(1);
    }
  }, [records, searchDate, recordsPerPage]);

  const loadUserData = async () => {
    try {
      const storageUser = await storage.getUser();
      if (storageUser) {
        const user = JSON.parse(storageUser);
        setUserData(user);
        loadHistoryData(user._id);
        loadWeeklyData(user._id);
      } else {
        enqueueSnackbar("User not found. Please login again.", { variant: "error" });
      }
    } catch (error) {
      console.error("Error loading user data:", error);
      enqueueSnackbar("Error loading user data. Please login again.", { variant: "error" });
    }
  };

  const loadHistoryData = async (userId) => {
    try {
      setIsLoading(true);
      const result = await user_api.get_limited_usersV4(userId, 'all');
      if (result && result.data) {
        setRecords(result.data);
        setFilteredRecords(result.data);
        setTotalPages(Math.ceil(result.data.length / recordsPerPage));
      } else {
        enqueueSnackbar("No history records found", { variant: "info" });
        setRecords([]);
        setFilteredRecords([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      enqueueSnackbar("Failed to load history. Please try again.", { variant: "error" });
      setRecords([]);
      setFilteredRecords([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  const loadWeeklyData = async (userId) => {
    try {
      setIsLoading(true);
      const result = await user_api.get_limited_usersV4(userId, 7);
      if (result && result.data) {
        let totalHours = 0;
        
        // Calculate total weekly hours
        result.data.slice(0, 7).forEach((record) => {
          const timeIn = filterByStatus(record.record, 'time-in').time;
          const timeOut = filterByStatus(record.record, 'time-out').time;

          if (timeOut !== 'n/a') {
            const timeOutDate = moment(timeOut, "h:mm A").toDate();
            const timeInDate = moment(timeIn, "h:mm A").toDate();
            const diffInHours = moment.duration(timeOutDate.getTime() - timeInDate.getTime()).asHours();
            totalHours += diffInHours;
          }
        });
        
        setTotalWeeklyHours(parseFloat(totalHours).toFixed(2));
        setWeeklyRecords(result.data.slice(0, 7));
      } else {
        enqueueSnackbar("No weekly records found", { variant: "info" });
        setWeeklyRecords([]);
      }
    } catch (error) {
      console.error('Error fetching weekly data:', error);
      enqueueSnackbar("Failed to load weekly data. Please try again.", { variant: "error" });
      setWeeklyRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchPage = (pageIndex) => {
    if (pageIndex === 0) {
      setSummaryPage(true);
      setAllShiftPage(false);
    } else {
      setSummaryPage(false);
      setAllShiftPage(true);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleClearSearch = () => {
    setSearchDate(null);
  };

  // Format date to match dashboard format
  const formatRecordDate = (dateString) => {
    return moment(dateString).format('MMM DD, YYYY');
  };

  const renderTime = (_time) => {
    // First convert any numeric timestamp to a Date object
    let _date = new Date(_time);
    // Check if conversion was successful (will be NaN for invalid dates)
    if (!isNaN(_date.getTime())) {
      const userTimeZone = moment.tz.guess();
      return _date.toLocaleString('en-US', { timeZone: userTimeZone, hour: 'numeric', minute: '2-digit' });
    }
    // Return original time if not a valid timestamp/date
    return _time;
  };

  const filterByStatus = (_data, type) => {
    let _d = _data.filter((_d) => (_d.status === type ? _d : ''));
    if (_d.length > 0) {
      _d = _d[0];
    }
    if (_d) {
      if (typeof(_d.time) === "string") {
        return {
          time: _d.time === undefined ? 'n/a' : _d.time,
          location: _d.address === undefined ? 'n/a' : _d.address,
          workmate: _d.workmate === undefined ? '' : 'Workmate( ' + _d.workmate + ' )'
        };
      } else {
        return {
          time: _d.time === undefined ? 'n/a' : renderTime(_d.time),
          location: _d.address === undefined ? 'n/a' : _d.address,
          workmate: _d.workmate === undefined ? '' : 'Workmate( ' + _d.workmate + ' )'
        };
      }
    } else {
      return { time: 'n/a', location: 'n/a', workmate: '' };
    }
  };

  const handleCopyProcessId = (id) => {
    navigator.clipboard.writeText(id)
      .then(() => {
        enqueueSnackbar("Process ID copied to clipboard", { variant: "success" });
      })
      .catch((err) => {
        console.error("Failed to copy process ID:", err);
        enqueueSnackbar("Failed to copy process ID", { variant: "error" });
      });
  };

  const toggleCardExpand = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const convertToAmPm = (time) => {
    
    // Check if the input time is the string "N/A"
    if (time === "N/A") {
      return "N/A";
    }

    let [hours, minutes] = time.split(":").map(Number);
    let period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // Convert 0H to 12 AM, and 12H stays 12 PM
    
    return `${hours}:${minutes.toString().padStart(2, "0")} ${period}`;
  }

  // Single render function for history cards
  const renderHistoryCard = (record, isWeekly = false) => {
    const timeIn = filterByStatus(record.record, 'time-in').time;
    const breakIn = filterByStatus(record.record, 'break-in').time;
    const breakOut = filterByStatus(record.record, 'break-out').time;
    const timeOut = filterByStatus(record.record, 'time-out').time;
    const hoursWorked = record.hoursWorked;
    
    // Calculate late minutes for weekly records
    let lateMinutes = 0;
    if (isWeekly && record.startShift && timeIn !== 'n/a') {
      try {
        const scheduleStartTime = moment(record.startShift, "h:mm A");
        const timeInMoment = moment(timeIn, "h:mm A");
        
        if (timeInMoment.isAfter(scheduleStartTime)) {
          lateMinutes = timeInMoment.diff(scheduleStartTime, 'minutes');
        }
      } catch (error) {
        console.error("Error calculating late minutes:", error);
      }
    }

    // Format schedule times - handle both 24h and 12h formats
    const formatScheduleTime = (time) => {
      if (!time) return 'N/A';
      if (time.includes(' ')) return time; // Already in 12h format
      return convertToAmPm(time);
    };

    const scheduleStart = formatScheduleTime(record.startShift);
    const scheduleEnd = formatScheduleTime(record.endShift);
    const breakDuration = record.breakMinutes || '0';

    return (
      <Card 
        key={record._id}
        className={classes.historyCard}
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          border: '1px solid #2194F3',
          borderRadius: '8px',
        }}
      >
        <CardContent sx={{ p: 2 }}>
          {/* First card section - Process ID */}
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              Process ID: {record._id}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => handleCopyProcessId(record._id)}
              sx={{ ml: 1 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Second card section - Blue header section */}
          <Box sx={{ 
            mb: 1, 
            pb: 1, 
            borderBottom: '1px solid #eee',
            backgroundColor: '#2194F3',
            borderRadius: '4px',
            p: 1,
            width: '100%'
          }}>
            <Grid container spacing={0.5} sx={{ width: '100%', mb: 1 }}>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" component="div" color="white" sx={{ opacity: 0.9 }}>Date</Typography>
                <Typography variant="body2" color="white">{formatRecordDate(record.date)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" component="div" color="white" sx={{ opacity: 0.9 }}>Position</Typography>
                <Typography variant="body2" color="white">{record.position || 'N/A'}</Typography>
              </Grid>
            </Grid>
            <Grid container spacing={0.5} sx={{ width: '100%' }}>
              <Grid item xs={4} sm={4}>
                <Typography variant="caption" component="div" color="white" sx={{ opacity: 0.9 }}>Start Shift</Typography>
                <Typography variant="body2" color="white">{scheduleStart}</Typography>
              </Grid>
              <Grid item xs={4} sm={4}>
                <Typography variant="caption" component="div" color="white" sx={{ opacity: 0.9 }}>End Shift</Typography>
                <Typography variant="body2" color="white">{scheduleEnd}</Typography>
              </Grid>
              <Grid item xs={4} sm={4}>
                <Typography variant="caption" component="div" color="white" sx={{ opacity: 0.9 }}>Break (min)</Typography>
                <Typography variant="body2" color="white">{breakDuration}</Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Third card section - Time records and late minutes */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={4} sm={4}>
              <Typography variant="caption" component="div" color="text.secondary">Time In</Typography>
              <Typography variant="body2" color="primary.main">
                {timeIn || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Typography variant="caption" component="div" color="text.secondary">Break In</Typography>
              <Typography variant="body2" color="success.main">
                {breakIn || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Typography variant="caption" component="div" color="text.secondary">Break Out</Typography>
              <Typography variant="body2" color="success.main">
                {breakOut || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Typography variant="caption" component="div" color="text.secondary">Time Out</Typography>
              <Typography variant="body2" color="error.main">
                {timeOut || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Typography variant="caption" component="div" color="text.secondary">Late (min)</Typography>
              <Typography variant="body2" color="warning.main">
                {isWeekly ? lateMinutes : (record.lateMinutes || '0')}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Typography variant="caption" component="div" color="text.secondary">Total Hours</Typography>
              <Typography variant="body2" color="info.main">
                {hoursWorked}
              </Typography>
            </Grid>
          </Grid>

          {/* Fourth card section - Premium Hours (Collapsible) */}
          <Box sx={{ 
            borderTop: '1px solid #eee',
            pt: 2,
            position: 'relative'
          }}>
            <Box sx={{
              justifyContent: 'space-between',
              display: 'flex',
              alignItems: 'baseline',
              mb: 1
            }}>
              {expandedCards[record._id] ? (
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={6}>
                    <Typography variant="caption" component="div" color="text.secondary">Overtime (hrs)</Typography>
                    <Typography variant="body2" color="secondary.main">
                      {record.overtime || '0'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography variant="caption" component="div" color="text.secondary">Night Shift (hrs)</Typography>
                    <Typography variant="body2" color="secondary.main">
                      {record.nightShift || '0'}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={6}>
                    <Typography variant="caption" component="div" color="text.secondary">Rest Day (hrs)</Typography>
                    <Typography variant="body2" color="secondary.main">
                      {record.restday || '0'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <Typography variant="caption" component="div" color="text.secondary">Holiday (hrs)</Typography>
                    <Typography variant="body2" color="secondary.main">
                      {record.holidayHours || '0'}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  Premium Hours
                </Typography>
              )}
              
              <Button 
                size="small" 
                onClick={() => toggleCardExpand(record._id)}
                endIcon={<Icon icon="mdi:chevron-down" style={{ transform: expandedCards[record._id] ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
              >
                {expandedCards[record._id] ? 'Hide' : 'View'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Get current records
  const getCurrentRecords = () => {
    const startIndex = (page - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return filteredRecords.slice(startIndex, endIndex);
  };

  return (
    <Page title="My History">
      <Container maxWidth="xl" className={classes.root}>
        <Typography variant="h4" gutterBottom>
          My History
        </Typography>

        {/* Tab buttons */}
        <Paper elevation={0} sx={{ mb: 3 }}>
          <Toolbar 
            sx={{
              backgroundColor: '#2194F3',
              borderRadius: '8px',
              mb: 3
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                width: '100%'
              }}
            >
              <Box
                onClick={() => handleSwitchPage(0)}
                className={classes.tabButton}
              >
                <HistoryIcon style={{ fontSize: summaryPage ? 45 : 30, color: summaryPage ? 'white' : '#d4d4d4' }} />
                <Typography variant='caption' style={{ color: summaryPage ? 'white' : '#d4d4d4' }}> Weekly Summary </Typography>
              </Box>
              <Box
                onClick={() => handleSwitchPage(1)}
                className={classes.tabButton}
              >
                <DateRangeIcon style={{ fontSize: allShiftPage ? 45 : 30, color: allShiftPage ? 'white' : '#d4d4d4' }} />
                <Typography variant="caption" style={{ color: allShiftPage ? 'white' : '#d4d4d4' }}> All My Shifts </Typography>
              </Box>
            </Box>
          </Toolbar>
        </Paper>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {summaryPage && (
              <Box sx={{ width: '100%' }}>
                
                {weeklyRecords && weeklyRecords.length > 0 ? (
                  weeklyRecords.map((record) => renderHistoryCard(record, true))
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: 200,
                    border: '1px dashed #ccc',
                    borderRadius: '8px'
                  }}>
                    <Typography color="text.secondary">No weekly records available</Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {allShiftPage && (
              <Box sx={{ width: '100%' }}>
                {/* Search and date filter */}
                <Box className={classes.searchContainer}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      label="Search by date"
                      value={searchDate}
                      onChange={(date) => setSearchDate(date)}
                      renderInput={(params) => <TextField {...params} size="small" sx={{ flexGrow: 5 }} />}
                    />
                  </LocalizationProvider>
                  
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleClearSearch}
                    sx={{ flexGrow: 1 }}
                    disabled={!searchDate}
                  >
                    {isFiltered ? "Show All" : "Clear"}
                  </Button>
                </Box>
                
                {filteredRecords.length > 0 ? (
                  <>
                    {getCurrentRecords().map((record) => renderHistoryCard(record, false))}
                    
                    {/* Pagination */}
                    <Box className={classes.paginationContainer}>
                      <Pagination 
                        count={totalPages} 
                        page={page} 
                        onChange={handleChangePage} 
                        color="primary" 
                        showFirstButton 
                        showLastButton
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: 200,
                    border: '1px dashed #ccc',
                    borderRadius: '8px'
                  }}>
                    <Typography color="text.secondary">
                      {isFiltered ? "No records found for selected date" : "No records available"}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Container>
    </Page>
  );
};

export default EmployeeMyHistory;
